import ApiServiceV2 from './apiService/apiServiceV2';

const positiveId = value => {
	const text = String(value);
	if (!/^[1-9][0-9]*$/.test(text) || !Number.isSafeInteger(Number(text)))
		throw new Error('INVALID_DOCUMENT_ID');
	return text;
};

// The caller supplies domain IDs, never a storage URL or token. Existing API
// authentication stays in its established Authorization header implementation.
function openPrivatePdf({
	url,
	api = ApiServiceV2,
	viewer = window,
	urls = URL,
	Controller = AbortController,
	timeoutMs = 125000,
} = {}) {
	const controller = new Controller();
	const popup = viewer.open('about:blank', '_blank');
	if (!popup) throw new Error('PDF_VIEWER_BLOCKED');
	popup.opener = null;
	let finished = false;
	let cancelled = false;
	let objectUrl = null;
	let cleanupTimer = null;
	let onLoad = null;
	const revoke = () => {
		if (onLoad) popup.removeEventListener('load', onLoad);
		onLoad = null;
		if (objectUrl) urls.revokeObjectURL(objectUrl);
		objectUrl = null;
		clearTimeout(cleanupTimer);
	};
	const cancel = () => {
		if (finished || cancelled) return;
		cancelled = true;
		controller.abort();
		clearTimeout(timer);
		revoke();
		popup.close();
	};
	const timer = setTimeout(cancel, timeoutMs);
	const done = (async () => {
		try {
			const blob = await api.getBlob({ url, controlSignal: controller.signal });
			if (cancelled || popup.closed) throw new Error('PDF_DOWNLOAD_CANCELLED');
			if (
				!blob ||
				blob.type !== 'application/pdf' ||
				blob.size < 1 ||
				blob.size > 160 * 1024 * 1024
			)
				throw new Error('PDF_RESPONSE_REJECTED');
			objectUrl = urls.createObjectURL(blob);
			const openedUrl = objectUrl;
			onLoad = () => {
				try {
					if (popup.location.href === openedUrl) revoke();
				} catch (_) {
					/* fallback cleanup below */
				}
			};
			popup.addEventListener('load', onLoad);
			popup.location.replace(objectUrl);
			// Keep an already opened PDF viewer alive if the modal unmounts. The
			// bounded cleanup also covers viewers without a usable load callback.
			if (objectUrl) cleanupTimer = setTimeout(revoke, 60000);
			finished = true;
			return { opened: true };
		} catch (_) {
			cancel();
			throw new Error('PRIVATE_PDF_UNAVAILABLE');
		} finally {
			clearTimeout(timer);
		}
	})();
	return {
		done,
		cancel,
		get pending() {
			return !finished && !cancelled;
		},
	};
}

export function openServiceTaskPdf({ otdId, serviceTaskId, ...options } = {}) {
	return openReportCapability({
		...options, kind: 'task', documentId: positiveId(otdId), taskId: positiveId(serviceTaskId),
		url: `/orders/reports/${positiveId(otdId)}/${positiveId(serviceTaskId)}/pdf/url`,
	});
}

export function openBillingSummaryPdf({
	type,
	id,
	organizationId,
	excludePayrollUsers = false,
	...options
} = {}) {
	if (!['paycut', 'payment'].includes(type))
		throw new Error('INVALID_DOCUMENT_ID');
	const url =
		type === 'payment'
			? `/payments/pdf/${positiveId(organizationId)}/${positiveId(id)}`
			: `/paycuts/pdf/${positiveId(organizationId)}/${positiveId(id)}/${excludePayrollUsers === true ? 'exclude' : 'include'}-payroll-users`;
	return openPrivatePdf({ ...options, url });
}

const canonicalTime = value => {
	const parsed = typeof value === 'string' ? Date.parse(value) : NaN;
	return Number.isFinite(parsed) && new Date(parsed).toISOString() === value ? parsed : NaN;
};
function reportCapability(value, kind, documentId, taskId) {
	const url = new URL(value), q = url.searchParams;
	if (url.href !== value || url.protocol !== 'https:' || url.username || url.password || url.href.includes('#') ||
		!['av4cus-synapse-pilot-assets-270254976539-use2.s3.us-east-2.amazonaws.com',
			'av4cus-synapse-pilot-assets-270254976539-use2.s3.amazonaws.com'].includes(url.host)) throw Error('INVALID_RESPONSE');
	const prefix = '^/(production|development)/[A-Za-z0-9_-]+/[A-Za-z0-9_-]+/reports/';
	const filename = kind === 'task' ? taskId + '\\.pdf$' : 'all\\.pdf$';
	const match = new RegExp(prefix + (kind === 'site'
		? 'contracts/([1-9][0-9]*)/[A-Za-z0-9_-]+\\.pdf$'
		: '(services|service_tasks)/([1-9][0-9]*)/([1-9][0-9]*)/' + filename)).exec(url.pathname);
	if (!match || (kind !== 'site'
		? !Number.isSafeInteger(Number(match[3])) || match[4] !== documentId
		: !Number.isSafeInteger(Number(match[2])))) throw Error('INVALID_RESPONSE');
	const allowed = new Set(['X-Amz-Algorithm', 'X-Amz-Credential', 'X-Amz-Date', 'X-Amz-Expires',
		'X-Amz-Signature', 'X-Amz-SignedHeaders', 'x-amz-expected-bucket-owner', 'versionId', 'X-Amz-Security-Token']);
	const seen = new Set();
	for (const [key, item] of q.entries()) {
		if (!allowed.has(key) || seen.has(key) || !item || /[\x00-\x1f\x7f]/.test(item)) throw Error('INVALID_RESPONSE');
		seen.add(key);
	}
	const date = q.get('X-Amz-Date'), seconds = q.get('X-Amz-Expires'), credential = q.get('X-Amz-Credential');
	if (!url.search.slice(1).split('&').every(part => /^[A-Za-z0-9-]+=/.test(part)) ||
		q.get('X-Amz-Algorithm') !== 'AWS4-HMAC-SHA256' || q.get('X-Amz-SignedHeaders') !== 'host' ||
		q.get('x-amz-expected-bucket-owner') !== '270254976539' || !/^[a-f0-9]{64}$/.test(q.get('X-Amz-Signature')) ||
		!/^\d{8}T\d{6}Z$/.test(date) || !/^[1-9][0-9]*$/.test(seconds) || Number(seconds) > 300 ||
		!/^[A-Z0-9_]+\/\d{8}\/us-east-2\/s3\/aws4_request$/.test(credential) || credential.split('/')[1] !== date.slice(0, 8) ||
		(q.has('versionId') && q.get('versionId').length > 1024)) throw Error('INVALID_RESPONSE');
	const signedAt = canonicalTime(date.replace(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/, '$1-$2-$3T$4:$5:$6.000Z'));
	if (!Number.isFinite(signedAt)) throw Error('INVALID_RESPONSE');
	return { url, expiresAt: signedAt + Number(seconds) * 1000 };
}

// A fresh capability for the current DB-associated consolidated report. Only
// JSON passes through API; the browser PDF viewer downloads directly from S3.
export function openMergedReportPdf({ otdId, kind = 'service', ...options } = {}) {
	const documentId = positiveId(otdId);
	if (!['service', 'site'].includes(kind)) throw new Error('INVALID_DOCUMENT_ID');
	const path = kind === 'site' ? 'getMergedContractReportUrl' : 'getMergedReportUrl';
	return openReportCapability({ ...options, documentId, kind,
		url: `/collectorManager/v1/${path}/${documentId}/url` });
}
function openReportCapability({ url: endpoint, documentId, taskId, kind,
	api = ApiServiceV2, viewer = window, Controller = AbortController, monotonicNow, timeoutMs = 20000,
} = {}) {
	const clock = monotonicNow || (typeof performance !== 'undefined' && typeof performance.now === 'function'
		? () => performance.now() : () => Date.now());
	const context = () => {
		try { const p = JSON.parse(localStorage.getItem('synapse_profile'));
			return JSON.stringify([p?.user?.id, p?.user?.profileId, p?.entity?.id, p?.organization?.id, p?.company?.id]);
		} catch (_) { return null; }
	};
	const token = localStorage.getItem('synapse_token'), profile = context();
	const isCurrent = () => profile !== null && token === localStorage.getItem('synapse_token') && profile === context();
	const controller = new Controller(),
		popup = viewer.open('about:blank', '_blank');
	if (!popup) throw new Error('PDF_VIEWER_BLOCKED');
	popup.opener = null;
	let finished = false,
		cancelled = false,
		rejectCancelled;
	const cancelledWait = new Promise((_, reject) => {
		rejectCancelled = reject;
	});
	cancelledWait.catch(() => {});
	const cancel = () => {
		if (!finished && !cancelled) {
			cancelled = true;
			controller.abort();
			popup.close();
			rejectCancelled(new Error('CANCELLED'));
		}
	};
	const timer = setTimeout(cancel, timeoutMs);
	const done = (async () => {
		try {
			for (let attempt = 0; attempt < 2; attempt++) {
				if (!isCurrent()) throw Error('CANCELLED');
				const started = clock();
				if (!Number.isFinite(started)) throw Error('INVALID_CLOCK');
				const response = await Promise.race([
					api.get({
						url: endpoint,
						controlSignal: controller.signal,
					}),
					cancelledWait,
				]);
				if (cancelled || popup.closed || !isCurrent()) throw new Error('CANCELLED');
				const elapsed = clock() - started, server = canonicalTime(response && response.serverTime),
					expires = canonicalTime(response && response.expiresAt), ttl = expires - server;
				if (!response || !Number.isSafeInteger(response.bytes) || response.bytes < 5 || response.bytes > 256 * 1024 * 1024 ||
					!Number.isFinite(ttl) || ttl > 300000 || !Number.isFinite(elapsed) || elapsed < 0 || elapsed > timeoutMs) throw Error('INVALID_RESPONSE');
				const capability = reportCapability(response.url, kind, documentId, taskId), url = capability.url;
				const left = Math.min(expires, capability.expiresAt) - server - elapsed;
				if (left <= 5000 && attempt === 0) continue;
				if (left <= 5000) throw Error('EXPIRED');
				if (!isCurrent()) throw Error('CANCELLED');
				popup.location.replace(url.href);
				finished = true;
				return { opened: true };
			}
			throw new Error('EXPIRED');
		} catch (_) {
			cancel();
			throw new Error('PRIVATE_PDF_UNAVAILABLE');
		} finally {
			clearTimeout(timer);
		}
	})();
	return {
		done,
		cancel,
		get pending() {
			return !finished && !cancelled;
		},
	};
}
